import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  X, Link2, Copy, Check, Clock, Trash2, AlertCircle,
  MessageCircle, Mail, Share2, RefreshCw,
} from "lucide-react";
import api from "../utils/api";

const BASE_URL = window.location.origin;

const EXPIRY_OPTIONS = [
  { value: "1h",    label: "1 Hour",   short: "1h"    },
  { value: "24h",   label: "24 Hours", short: "24h"   },
  { value: "7d",    label: "7 Days",   short: "7d"    },
  { value: "never", label: "No Expiry", short: "∞"    },
];

function fmtExpiry(expiresAt) {
  if (!expiresAt) return "Never expires";
  const d   = new Date(expiresAt);
  const now = new Date();
  if (d < now) return "Expired";
  const diffMs = d - now;
  const diffH  = Math.floor(diffMs / 3600000);
  const diffD  = Math.floor(diffMs / 86400000);
  if (diffH < 1)  return "Expires in < 1h";
  if (diffH < 24) return `Expires in ${diffH}h`;
  return `Expires in ${diffD}d`;
}

function isExpired(share) {
  if (share.revoked) return true;
  if (!share.expires_at) return false;
  return new Date(share.expires_at) < new Date();
}

function getShareUrl(rawToken) {
  return `${BASE_URL}/share/${rawToken}`;
}

// ── Copy button with visual feedback ─────────────────────────────────────────
function CopyButton({ text, label = "Copy" }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }
  return (
    <button
      onClick={copy}
      style={{
        display: "flex", alignItems: "center", gap: 5,
        background: copied ? "#F0FDF4" : "#F5F5F5",
        border: `1px solid ${copied ? "#BBF7D0" : "#E5E7EB"}`,
        borderRadius: 7, padding: "5px 10px",
        fontSize: 12, fontWeight: 600,
        color: copied ? "#16A34A" : "#374151",
        cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap",
      }}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
      {copied ? "Copied!" : label}
    </button>
  );
}

// ── A single share row ────────────────────────────────────────────────────────
function ShareRow({ share, shareUrl, onRevoke, revoking }) {
  const expired  = isExpired(share);
  const statusBg     = expired ? "#F9FAFB" : "#F0FDF4";
  const statusColor  = expired ? "#9CA3AF" : "#16A34A";
  const statusBorder = expired ? "#E5E7EB" : "#BBF7D0";
  const statusLabel  = share.revoked ? "Revoked" : expired ? "Expired" : "Active";

  function shareWhatsApp() {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(`Track this vehicle live:\n${shareUrl}`)}`,
      "_blank", "noopener,noreferrer"
    );
  }
  function shareEmail() {
    window.location.href =
      `mailto:?subject=${encodeURIComponent("Vehicle Tracking Link")}&body=${encodeURIComponent(`Track the vehicle live:\n\n${shareUrl}`)}`;
  }

  return (
    <div style={{
      background: "#fff", border: "1px solid #F0F1F3", borderRadius: 10,
      padding: "12px 14px", opacity: expired ? 0.65 : 1,
    }}>
      {/* Link row */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div style={{
          flex: 1, background: "#F5F7FA", border: "1px solid #E5E7EB",
          borderRadius: 7, padding: "6px 10px",
          fontSize: 12, color: "#374151", fontFamily: "monospace",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {shareUrl}
        </div>
        <CopyButton text={shareUrl} />
      </div>

      {/* Meta row */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 4,
          background: statusBg, border: `1px solid ${statusBorder}`,
          borderRadius: 20, padding: "2px 8px",
          fontSize: 11, fontWeight: 600, color: statusColor,
        }}>
          <div style={{ width: 5, height: 5, borderRadius: "50%", background: statusColor }} />
          {statusLabel}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#9CA3AF" }}>
          <Clock size={10} />
          {fmtExpiry(share.expires_at)}
        </div>

        {share.access_count > 0 && (
          <div style={{ fontSize: 11, color: "#9CA3AF" }}>
            {share.access_count} view{share.access_count !== 1 ? "s" : ""}
          </div>
        )}

        {/* Actions — only if still active */}
        {!expired && (
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 5 }}>
            <button
              onClick={shareWhatsApp}
              title="Share via WhatsApp"
              style={{
                background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 7,
                padding: "5px 10px", display: "flex", alignItems: "center", gap: 4,
                fontSize: 12, fontWeight: 600, color: "#15803D", cursor: "pointer",
                transition: "all 0.15s",
              }}
              onMouseOver={e => { e.currentTarget.style.background="#DCFCE7"; }}
              onMouseOut={e  => { e.currentTarget.style.background="#F0FDF4"; }}
            >
              <MessageCircle size={11} /> WhatsApp
            </button>
            <button
              onClick={shareEmail}
              title="Share via Email"
              style={{
                background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 7,
                padding: "5px 10px", display: "flex", alignItems: "center", gap: 4,
                fontSize: 12, fontWeight: 600, color: "#2563EB", cursor: "pointer",
                transition: "all 0.15s",
              }}
              onMouseOver={e => { e.currentTarget.style.background="#DBEAFE"; }}
              onMouseOut={e  => { e.currentTarget.style.background="#EFF6FF"; }}
            >
              <Mail size={11} /> Email
            </button>
            <button
              onClick={() => onRevoke(share.id)}
              disabled={revoking === share.id}
              title="Revoke link"
              style={{
                background: "none", border: "1px solid #E5E7EB", borderRadius: 7,
                padding: "5px 8px", display: "flex", alignItems: "center",
                color: "#9CA3AF", cursor: revoking === share.id ? "not-allowed" : "pointer",
                transition: "all 0.15s",
              }}
              onMouseOver={e => { e.currentTarget.style.color="#EF4444"; e.currentTarget.style.borderColor="#FECACA"; }}
              onMouseOut={e  => { e.currentTarget.style.color="#9CA3AF"; e.currentTarget.style.borderColor="#E5E7EB"; }}
            >
              {revoking === share.id
                ? <RefreshCw size={12} style={{ animation: "spin 0.7s linear infinite" }} />
                : <Trash2 size={12} />}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────────
export default function ShareModal({ vehicle, onClose }) {
  const [shares,      setShares]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [creating,    setCreating]    = useState(false);
  const [revoking,    setRevoking]    = useState(null);
  const [expiresIn,   setExpiresIn]   = useState("24h");
  const [newShare,    setNewShare]     = useState(null); // just-created share with raw token
  const [error,       setError]       = useState(null);
  const [showExpired, setShowExpired] = useState(false);

  const loadShares = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/shares?vehicleId=${vehicle.id}`);
      setShares(data);
    } catch {
      setError("Failed to load shares.");
    } finally {
      setLoading(false);
    }
  }, [vehicle.id]);

  useEffect(() => { loadShares(); }, [loadShares]);

  async function handleCreate() {
    setCreating(true);
    setError(null);
    setNewShare(null);
    try {
      const { data } = await api.post("/api/shares", {
        vehicleId: vehicle.id,
        expiresIn,
      });
      setNewShare(data);     // data.token = raw token (only returned once)
      await loadShares();    // refresh list
    } catch (err) {
      setError(err.message || "Failed to create share link.");
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(shareId) {
    setRevoking(shareId);
    try {
      await api.delete(`/api/shares/${shareId}`);
      setShares(prev => prev.map(s => s.id === shareId ? { ...s, revoked: true } : s));
    } catch {
      setError("Failed to revoke share.");
    } finally {
      setRevoking(null);
    }
  }

  const activeShares  = shares.filter(s => !isExpired(s));
  const expiredShares = shares.filter(s => isExpired(s));

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: "fixed", inset: 0, zIndex: 100,
        background: "rgba(15,23,42,0.6)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ scale: 0.94, y: 20, opacity: 0 }}
        animate={{ scale: 1,    y: 0,  opacity: 1 }}
        exit={{    scale: 0.94, y: 10, opacity: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        style={{
          width: "100%", maxWidth: 480,
          background: "#fff", borderRadius: 18,
          boxShadow: "0 32px 80px rgba(0,0,0,0.22)",
          border: "1px solid #EBEBEB",
          overflow: "hidden", maxHeight: "92vh",
          display: "flex", flexDirection: "column",
        }}
      >
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

        {/* Header */}
        <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid #F0F1F3", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{
                  width: 30, height: 30, borderRadius: 8,
                  background: "#EEF2FF", border: "1px solid #C7D2FE",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Share2 size={14} color="#4F46E5" />
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>Share Vehicle</div>
              </div>
              <div style={{ fontSize: 13, color: "#6B7280", marginTop: 4 }}>
                <span style={{
                  fontFamily: "monospace", fontSize: 11, fontWeight: 700,
                  background: "#F7F8F9", border: "1px solid #E5E7EB",
                  borderRadius: 4, padding: "1px 6px", marginRight: 6,
                }}>
                  {vehicle.plateNumber}
                </span>
                {vehicle.nickname !== vehicle.plateNumber && vehicle.nickname}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: "#9CA3AF", padding: 4, display: "flex", borderRadius: 6,
              }}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px 20px", display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Error */}
          {error && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              background: "#FEF2F2", border: "1px solid #FECACA",
              borderRadius: 8, padding: "10px 12px",
              fontSize: 13, color: "#DC2626",
            }}>
              <AlertCircle size={14} style={{ flexShrink: 0 }} />
              {error}
            </div>
          )}

          {/* Newly created share — show with raw token */}
          {newShare && (
            <div style={{
              background: "#F0FDF4", border: "1px solid #BBF7D0",
              borderRadius: 10, padding: "12px 14px",
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#15803D", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                <Check size={14} /> Link created successfully!
              </div>
              <div style={{
                background: "#fff", border: "1px solid #D1FAE5",
                borderRadius: 7, padding: "8px 10px",
                fontSize: 12, color: "#374151", fontFamily: "monospace",
                wordBreak: "break-all", marginBottom: 8, lineHeight: 1.5,
              }}>
                {getShareUrl(newShare.token)}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <CopyButton text={getShareUrl(newShare.token)} label="Copy Link" />
                <button
                  onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Track this vehicle live:\n${getShareUrl(newShare.token)}`)}`, "_blank", "noopener,noreferrer")}
                  style={{
                    display: "flex", alignItems: "center", gap: 5,
                    background: "#F0FDF4", border: "1px solid #BBF7D0",
                    borderRadius: 7, padding: "5px 10px",
                    fontSize: 12, fontWeight: 600, color: "#15803D", cursor: "pointer",
                  }}
                >
                  <MessageCircle size={11} /> WhatsApp
                </button>
                <button
                  onClick={() => {
                    window.location.href = `mailto:?subject=${encodeURIComponent("Vehicle Tracking Link")}&body=${encodeURIComponent(`Track the vehicle live:\n\n${getShareUrl(newShare.token)}`)}`;
                  }}
                  style={{
                    display: "flex", alignItems: "center", gap: 5,
                    background: "#EFF6FF", border: "1px solid #BFDBFE",
                    borderRadius: 7, padding: "5px 10px",
                    fontSize: 12, fontWeight: 600, color: "#2563EB", cursor: "pointer",
                  }}
                >
                  <Mail size={11} /> Email
                </button>
              </div>
              <div style={{ fontSize: 11, color: "#6B7280", marginTop: 8 }}>
                This link is read-only. Guests cannot modify any data.
              </div>
            </div>
          )}

          {/* Create new share */}
          <div style={{
            background: "#F8FAFC", border: "1px solid #E5E7EB", borderRadius: 10, padding: "14px",
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 12 }}>
              Generate New Share Link
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 6 }}>Link expires after</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {EXPIRY_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setExpiresIn(opt.value)}
                    style={{
                      padding: "6px 14px", borderRadius: 7, fontSize: 12, fontWeight: 600,
                      cursor: "pointer", transition: "all 0.12s",
                      background: expiresIn === opt.value ? "#4F46E5" : "#fff",
                      color:      expiresIn === opt.value ? "#fff"    : "#374151",
                      border: expiresIn === opt.value ? "1.5px solid #4F46E5" : "1.5px solid #E5E7EB",
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={handleCreate}
              disabled={creating}
              style={{
                width: "100%", height: 40, borderRadius: 9,
                background: creating ? "#6B7280" : "#4F46E5",
                border: "none", color: "#fff",
                fontSize: 13, fontWeight: 700, cursor: creating ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                transition: "background 0.15s",
              }}
            >
              {creating
                ? <><RefreshCw size={13} style={{ animation: "spin 0.7s linear infinite" }} /> Generating…</>
                : <><Link2 size={13} /> Generate Link</>}
            </button>
          </div>

          {/* Active shares */}
          {loading ? (
            <div style={{ textAlign: "center", padding: 20, color: "#9CA3AF", fontSize: 13 }}>Loading…</div>
          ) : activeShares.length > 0 ? (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 8 }}>
                Active Links ({activeShares.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {activeShares.map(s => (
                  <ShareRow
                    key={s.id}
                    share={s}
                    shareUrl={`${BASE_URL}/share/[link hidden — copy from the creation panel above]`}
                    onRevoke={handleRevoke}
                    revoking={revoking}
                  />
                ))}
              </div>
              <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 8, lineHeight: 1.5 }}>
                Active links cannot be re-displayed for security. Copy immediately after creation.
              </div>
            </div>
          ) : !newShare ? (
            <div style={{ textAlign: "center", padding: "16px 0", color: "#9CA3AF", fontSize: 13 }}>
              No active share links. Generate one above.
            </div>
          ) : null}

          {/* Expired / revoked shares */}
          {expiredShares.length > 0 && (
            <div>
              <button
                onClick={() => setShowExpired(v => !v)}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: 13, fontWeight: 600, color: "#9CA3AF", padding: 0,
                }}
              >
                <Clock size={12} />
                {showExpired ? "Hide" : "Show"} expired/revoked links ({expiredShares.length})
              </button>
              {showExpired && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                  {expiredShares.map(s => (
                    <ShareRow
                      key={s.id}
                      share={s}
                      shareUrl={`${BASE_URL}/share/[revoked or expired]`}
                      onRevoke={handleRevoke}
                      revoking={revoking}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
